/**
 * ProgressBar — themed fill bar, the styled-components-free successor to
 * MSComponents/MSProgressBar.
 *
 * Keeps the MapleStory look (rounded track, gradient fill, optional
 * label/percentage row) but drives every colour/size from theme tokens.
 * `color` accepts a semantic name (health/mana/xp/success/danger) or any
 * explicit CSS colour string via `customColor`.
 */
import React from 'react';
import { COLORS, SPACE, RADII, FONT_SIZE, DURATION, EASING, CHROME } from '../theme';

export type ProgressBarColor =
  | 'health'
  | 'mana'
  | 'xp'
  | 'success'
  | 'danger';

export interface ProgressBarProps {
  value: number;
  max: number;
  label?: React.ReactNode;
  showPercentage?: boolean;
  /** Semantic fill colour. Ignored when `customColor` is provided. */
  color?: ProgressBarColor;
  /** Explicit fill colour — overrides `color`. */
  customColor?: string;
  /** Track height in px. */
  height?: number;
  /**
   * Ink-&-brass HUD treatment (style guide §2): rounded-pill ink track with an
   * inset shadow, and a glossed fill — one-light-source vertical gradient
   * (highlight top, ~shade bottom) + a 1px top gloss line + a subtly brighter
   * brass end-cap on the leading edge. Opt-in; the semantic fill colour is
   * untouched. Default (`false`) keeps the flat MapleStory look byte-for-byte,
   * so every other consumer (quest bars, Wave-2 windows, …) is a visual no-op.
   */
  glossed?: boolean;
}

function semanticColor(color: ProgressBarColor): string {
  switch (color) {
    case 'health': return COLORS.health;
    case 'mana': return COLORS.mana;
    case 'xp': return COLORS.xp;
    case 'success': return COLORS.success;
    case 'danger': return COLORS.danger;
  }
}

const captionStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: SPACE.xs,
  fontSize: FONT_SIZE.sm,
  color: COLORS.textSecondary,
};

const footerStyle: React.CSSProperties = {
  textAlign: 'right',
  marginTop: SPACE.xs,
  fontSize: FONT_SIZE.xs,
  color: COLORS.textMuted,
};

export function ProgressBar({
  value,
  max,
  label,
  showPercentage = true,
  color = 'health',
  customColor,
  height = 20,
  glossed = false,
}: ProgressBarProps): React.ReactElement {
  const safeMax = max > 0 ? max : 1;
  const percentage = Math.min(100, Math.max(0, (value / safeMax) * 100));
  const fill = customColor ?? semanticColor(color);

  const trackStyle: React.CSSProperties = glossed
    ? {
        background: 'rgba(0, 0, 0, 0.55)',
        borderRadius: RADII.pill,
        overflow: 'hidden',
        height,
        position: 'relative',
        boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.6)',
      }
    : {
        background: COLORS.surfaceSunken,
        borderRadius: RADII.lg,
        overflow: 'hidden',
        height,
        position: 'relative',
      };

  // Glossed fill (§2): the semantic fill colour is the base; a vertical overlay
  // gradient supplies the one-light-source highlight-top / shade-bottom without
  // needing a per-semantic "deep" hex, an inset box-shadow draws the 1px top
  // gloss line, and the brass end-cap SVG (theme/chrome) rides the leading edge.
  const barStyle: React.CSSProperties = glossed
    ? {
        width: `${percentage}%`,
        height: '100%',
        backgroundColor: fill,
        backgroundImage: `url("${CHROME.barEndCap({ color: 'rgba(255,255,255,0.85)', height })}"), linear-gradient(180deg, rgba(255,255,255,0.22) 0%, rgba(255,255,255,0) 45%, rgba(0,0,0,0.25) 100%)`,
        backgroundRepeat: 'no-repeat, no-repeat',
        backgroundPosition: 'right center, center',
        backgroundSize: 'auto 100%, 100% 100%',
        borderRadius: RADII.pill,
        boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.28)',
        transition: `width ${DURATION.slow} ${EASING.out}`,
      }
    : {
        width: `${percentage}%`,
        height: '100%',
        background: `linear-gradient(90deg, ${fill}, ${fill}cc)`,
        borderRadius: RADII.lg,
        transition: `width ${DURATION.slow} ${EASING.out}`,
      };

  return (
    <div>
      {label != null && (
        <div style={captionStyle}>
          <span>{label}</span>
          {showPercentage && <span>{Math.round(percentage)}%</span>}
        </div>
      )}
      <div style={trackStyle}>
        <div style={barStyle} />
      </div>
      {label == null && showPercentage && (
        <div style={footerStyle}>
          {value} / {max} ({Math.round(percentage)}%)
        </div>
      )}
    </div>
  );
}
